import { useCallback, useId, useMemo, useRef, useState } from 'react';
import ReactSelect, { components as ReactSelectComponents } from 'react-select';
import { Tooltip } from '../../admin/components/shared/Tooltip.tsx';
import { MenuWithStaticAction } from './SelectMenuStaticAction.tsx';

import './Select.css';

const DEFAULT_PREFIX = 'app-select';

function MultiCheckboxOption(optionProps) {
  const classNames = optionProps.selectProps?.multiCheckboxClassNames ?? {};
  return (
    <ReactSelectComponents.Option {...optionProps}>
      <div className={classNames.optionContent ?? ''}>
        <input
          type="checkbox"
          checked={optionProps.isSelected}
          readOnly
          tabIndex={-1}
          className={classNames.checkbox ?? ''}
          aria-hidden="true"
        />
        <span className={classNames.optionLabel ?? ''}>{optionProps.label}</span>
      </div>
    </ReactSelectComponents.Option>
  );
}

function MultiCheckboxValueContainer(containerProps) {
  const classNames = containerProps.selectProps?.multiCheckboxClassNames ?? {};
  const selectedOptions = containerProps.getValue?.() ?? [];
  const children = Array.isArray(containerProps.children)
    ? containerProps.children
    : [containerProps.children];
  const input = children[children.length - 1];
  const remainingCount = Math.max(0, selectedOptions.length - 1);
  const tooltipEnabled = Boolean(containerProps.selectProps?.multiCheckboxSummaryTooltip);
  const selectedLabels = selectedOptions.map((option) => option.label).join(', ');
  const tooltipContent = selectedOptions.length > 0 ? selectedLabels : null;
  const firstLabel = selectedOptions.length > 0 ? String(selectedOptions[0]?.label ?? '') : '';
  const summaryLayoutStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    minWidth: 0,
    maxWidth: '100%',
    flex: '0 1 auto',
    overflow: 'hidden',
  };
  const summaryNameStyle = {
    minWidth: 0,
    flex: '0 1 auto',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
  const summaryCountStyle = {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  };
  const summaryNode = firstLabel ? (
    <span
      className={classNames.summary ?? ''}
      style={summaryLayoutStyle}
      title={tooltipEnabled ? undefined : selectedLabels}
    >
      <span className={classNames.summaryName ?? ''} style={summaryNameStyle}>
        {firstLabel}
      </span>
      {remainingCount > 0 ? (
        <span className={classNames.summaryCount ?? ''} style={summaryCountStyle}>
          + {remainingCount} {remainingCount === 1 ? 'other' : 'others'}
        </span>
      ) : null}
    </span>
  ) : null;

  return (
    <ReactSelectComponents.ValueContainer {...containerProps}>
      {summaryNode ? (
        tooltipEnabled && tooltipContent ? (
          <Tooltip content={tooltipContent} side="top">
            {summaryNode}
          </Tooltip>
        ) : (
          summaryNode
        )
      ) : (
        children[0]
      )}
      {input}
    </ReactSelectComponents.ValueContainer>
  );
}

function HiddenMultiValue() {
  return null;
}

export function Select({
  options,
  value,
  onChange,
  disabled = false,
  isSearchable = false,
  isMulti = false,
  isMultiCheckbox = false,
  scrollBottomThrottleMs = 250,
  className = '',
  'aria-label': ariaLabel,
  ...props
}) {
  /**
   * Optional props (`placeholder`, `placeholderOnFocus`, `placeholderOnBlur`, `components`,
   * `onFocus`, `onBlur`, `onMenuScrollToBottom`, `menuPortalTarget`, `menuPosition`) are
   * intentionally read off `...props` instead of being destructured: TS infers JS destructured
   * keys (even with `= undefined`) as required, which would force every caller to pass them.
   */
  const placeholder = props.placeholder;
  const placeholderOnFocus = props.placeholderOnFocus;
  const placeholderOnBlur = props.placeholderOnBlur;
  const onMenuScrollToBottom = props.onMenuScrollToBottom;
  const menuPortalTargetProp = props.menuPortalTarget;
  const menuPositionProp = props.menuPosition;
  const userComponents = props.components;
  const menuStaticAction = props.menuStaticAction;
  const [isFocused, setIsFocused] = useState(false);
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const userOnFocus = props.onFocus;
  const userOnBlur = props.onBlur;
  const handleFocus = useCallback(
    (event) => {
      setIsFocused(true);
      if (typeof userOnFocus === 'function') userOnFocus(event);
    },
    [userOnFocus]
  );
  const handleBlur = useCallback(
    (event) => {
      setIsFocused(false);
      if (typeof userOnBlur === 'function') userOnBlur(event);
    },
    [userOnBlur]
  );
  const instanceId = useId();
  const lastScrollBottomCallAtRef = useRef(0);
  const selected = isMulti
    ? options.filter((o) => Array.isArray(value) && value.includes(o.value))
    : options.find((o) => o.value === value) ?? null;
  const resolvedPlaceholderOnFocus = placeholderOnFocus ?? 'Type something to search';
  const resolvedPlaceholderOnBlur =
    placeholderOnBlur ?? (typeof placeholder === 'string' ? placeholder : '');
  const hasDynamicPlaceholder =
    typeof placeholderOnFocus === 'string' || typeof placeholderOnBlur === 'string';
  const DynamicPlaceholder = useMemo(() => {
    if (!hasDynamicPlaceholder) return userComponents?.Placeholder;
    return (placeholderProps) => {
      const text = placeholderProps.isFocused
        ? resolvedPlaceholderOnFocus
        : resolvedPlaceholderOnBlur;
      return (
        <ReactSelectComponents.Placeholder {...placeholderProps}>
          {text}
        </ReactSelectComponents.Placeholder>
      );
    };
  }, [
    hasDynamicPlaceholder,
    resolvedPlaceholderOnFocus,
    resolvedPlaceholderOnBlur,
    userComponents,
  ]);
  const multiCheckboxComponents = useMemo(
    () =>
      isMulti && isMultiCheckbox
        ? {
            Option: MultiCheckboxOption,
            ValueContainer: MultiCheckboxValueContainer,
            MultiValue: HiddenMultiValue,
          }
        : {},
    [isMulti, isMultiCheckbox]
  );
  const mergedComponents = useMemo(() => {
    const merged = {
      ...multiCheckboxComponents,
      ...(userComponents ?? {}),
      ...(DynamicPlaceholder ? { Placeholder: DynamicPlaceholder } : {}),
    };
    if (menuStaticAction) {
      merged.Menu = MenuWithStaticAction;
    }
    return merged;
  }, [multiCheckboxComponents, userComponents, DynamicPlaceholder, menuStaticAction]);
  const userStyles = props.styles ?? {};
  const multiCheckboxStyles = useMemo(
    () =>
      isMulti && isMultiCheckbox
        ? {
            multiValue: (base) => ({ ...base, display: 'none' }),
            valueContainer: (base) => ({
              ...base,
              flexWrap: 'nowrap',
              overflow: 'hidden',
              alignItems: 'center',
            }),
            input: (base) => ({
              ...base,
              margin: 0,
              padding: 0,
            }),
            inputContainer: (base, state) => ({
              ...base,
              margin: 0,
              padding: 0,
              ...(state.selectProps.menuIsOpen
                ? { flex: '1 1 auto', minWidth: 2 }
                : { flex: '0 0 0', width: 0, minWidth: 0, overflow: 'hidden' }),
            }),
          }
        : {},
    [isMulti, isMultiCheckbox]
  );
  /** Project-wide placeholder gray (Tailwind `placeholder-gray-400`) — matches every other input placeholder in the app. */
  const DEFAULT_PLACEHOLDER_COLOR = '#9ca3af';
  const mergedStyles = {
    ...userStyles,
    ...Object.fromEntries(
      Object.entries(multiCheckboxStyles).map(([key, fn]) => [
        key,
        (base, state) => {
          const fromUser =
            typeof userStyles[key] === 'function' ? userStyles[key](base, state) : userStyles[key];
          return fn(fromUser ?? base, state);
        },
      ])
    ),
    placeholder: (base, state) => {
      const custom =
        typeof userStyles.placeholder === 'function'
          ? userStyles.placeholder(base, state)
          : userStyles.placeholder;
      /**
       * Default color applied *before* `custom` so callers (e.g. process picker)
       * can still override focus/idle colors explicitly.
       */
      return {
        ...base,
        color: DEFAULT_PLACEHOLDER_COLOR,
        ...(custom ?? {}),
      };
    },
  };
  const handleMenuScrollToBottom = useMemo(
    () => () => {
      if (typeof onMenuScrollToBottom !== 'function') return;
      const now = Date.now();
      if (now - lastScrollBottomCallAtRef.current < scrollBottomThrottleMs) return;
      lastScrollBottomCallAtRef.current = now;
      onMenuScrollToBottom();
    },
    [onMenuScrollToBottom, scrollBottomThrottleMs]
  );
  const handleChange = useCallback(
    (opt) => {
      if (isMulti) {
        const values = Array.isArray(opt) ? opt.map((item) => item.value) : [];
        onChange(values);
        return;
      }
      if (opt != null) onChange(opt.value);
      else onChange(null);
    },
    [isMulti, onChange]
  );

  const defaultPortal = typeof document !== 'undefined' ? document.body : null;
  const menuPortalTarget =
    menuPortalTargetProp !== undefined ? menuPortalTargetProp : defaultPortal;
  const menuPosition =
    menuPositionProp !== undefined
      ? menuPositionProp
      : menuPortalTarget
        ? 'fixed'
        : 'absolute';

  const resolvedControlShouldRenderValue =
    props.controlShouldRenderValue !== undefined
      ? props.controlShouldRenderValue
      : isMulti && isMultiCheckbox
        ? true
      : hasDynamicPlaceholder
        ? !isFocused || !menuIsOpen
        : true;
  const resolvedCloseMenuOnSelect =
    props.closeMenuOnSelect !== undefined
      ? props.closeMenuOnSelect
      : isMulti && isMultiCheckbox
        ? false
        : undefined;
  const resolvedHideSelectedOptions =
    props.hideSelectedOptions !== undefined
      ? props.hideSelectedOptions
      : isMulti && isMultiCheckbox
        ? false
        : undefined;
  const resolvedIsClearable =
    props.isClearable !== undefined
      ? props.isClearable
      : isMulti && isMultiCheckbox
        ? false
        : undefined;

  const propsRef = useRef(props);
  propsRef.current = props;

  const handleMenuOpen = useCallback(() => {
    setMenuIsOpen(true);
    const fn = propsRef.current?.onMenuOpen;
    if (typeof fn === 'function') fn();
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuIsOpen(false);
    const fn = propsRef.current?.onMenuClose;
    if (typeof fn === 'function') fn();
  }, []);

  return (
    <div
      className={['app-select-root', className].filter(Boolean).join(' ')}
      onClick={(e) => e.stopPropagation()}
    >
      <ReactSelect
        {...props}
        aria-label={ariaLabel}
        instanceId={instanceId}
        classNamePrefix={props.classNamePrefix ?? DEFAULT_PREFIX}
        isSearchable={isSearchable}
        isMulti={isMulti}
        options={options}
        value={selected}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        isDisabled={disabled}
        placeholder={placeholder}
        isClearable={resolvedIsClearable}
        controlShouldRenderValue={resolvedControlShouldRenderValue}
        closeMenuOnSelect={resolvedCloseMenuOnSelect}
        hideSelectedOptions={resolvedHideSelectedOptions}
        components={mergedComponents}
        styles={mergedStyles}
        onMenuScrollToBottom={handleMenuScrollToBottom}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        menuPortalTarget={menuPortalTarget}
        menuPosition={menuPosition}
      />
    </div>
  );
}
